@file:OptIn(org.jetbrains.compose.resources.InternalResourceApi::class)

package com.tovtalk.ui.theme

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.PlayArrow
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.ui.draw.clip
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.coroutines.delay
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import org.jetbrains.compose.resources.readResourceBytes
import com.tovtalk.utils.logger.Logger
import com.tovtalk.utils.config.isFontDebugEnabled

// Paths used by Compose resources for font loading (must match generated resource identity).
private const val FONT_REGULAR_PATH = "composeResources/tovtalk_kmp_v2.shared.generated.resources/font/OpenSans_Regular.ttf"
private const val FONT_BOLD_PATH = "composeResources/tovtalk_kmp_v2.shared.generated.resources/font/OpenSans_Bold.ttf"
private const val WEB_FONT_LATIN_REGULAR_ID = "web-open-sans-latin-regular"
private const val WEB_FONT_LATIN_BOLD_ID = "web-open-sans-latin-bold"
private const val WEB_FONT_HEBREW_REGULAR_ID = "web-open-sans-hebrew-regular"
private const val WEB_FONT_HEBREW_BOLD_ID = "web-open-sans-hebrew-bold"

/**
 * When false, we do not provide custom font (e.g. Skia/JS fails from resources).
 * When true on Web we load woff2 from Google (Latin + Hebrew).
 */
expect fun useCustomHebrewFontOnWeb(): Boolean

/** On Web: woff2 URLs (Latin + Hebrew). On other platforms: null. */
expect fun getWebFontLatinRegularUrl(): String?
expect fun getWebFontLatinBoldUrl(): String?
expect fun getWebFontHebrewRegularUrl(): String?
expect fun getWebFontHebrewBoldUrl(): String?
/** Fetches font bytes from URL (Web only). */
expect suspend fun loadFontBytesFromWebUrl(url: String): ByteArray?

/**
 * Web: loads Latin + Hebrew woff2 (or full TTF from resources if available), provides one font for entire app.
 * Android/iOS: loads full font from resources, provides LocalTovTalkFontFamily + LocalTovTalkHebrewFontFamily.
 *
 * When [debugFont] is true (Web: localStorage["tovtalk_debug_font"]=1 or ?debug_font=1), logs font loading and warmup to console.
 */
@Composable
fun TovTalkWithHebrewFont(
    debugFont: Boolean = false,
    content: @Composable () -> Unit
) {
    if (!useCustomHebrewFontOnWeb()) {
        content()
        return
    }

    var latinRegularBytes by remember { mutableStateOf<ByteArray?>(null) }
    var latinBoldBytes by remember { mutableStateOf<ByteArray?>(null) }
    var hebrewRegularBytes by remember { mutableStateOf<ByteArray?>(null) }
    var hebrewBoldBytes by remember { mutableStateOf<ByteArray?>(null) }
    var resourceRegularBytes by remember { mutableStateOf<ByteArray?>(null) }
    var resourceBoldBytes by remember { mutableStateOf<ByteArray?>(null) }

    val hasWebUrls = getWebFontHebrewRegularUrl() != null && getWebFontLatinRegularUrl() != null
    if (debugFont && isFontDebugEnabled()) {
        Logger.d("TovTalkWithHebrewFont: hasWebUrls=$hasWebUrls, loading fonts from URL", "FontDebug")
    }
    LaunchedEffect(hasWebUrls) {
        withContext(Dispatchers.Default) {
            try {
                if (hasWebUrls) {
                    // On Web use only woff2 from URL — Skiko cannot use TTF from resources (runtime "Failed to load font").
                    latinRegularBytes = getWebFontLatinRegularUrl()?.let { loadFontBytesFromWebUrl(it) }
                    latinBoldBytes = getWebFontLatinBoldUrl()?.let { loadFontBytesFromWebUrl(it) }
                    hebrewRegularBytes = getWebFontHebrewRegularUrl()?.let { loadFontBytesFromWebUrl(it) }
                    hebrewBoldBytes = getWebFontHebrewBoldUrl()?.let { loadFontBytesFromWebUrl(it) }
                    if (debugFont && isFontDebugEnabled()) {
                        Logger.d("TovTalkWithHebrewFont: all 4 woff2 loaded (Latin+Hebrew)", "FontDebug")
                    }
                } else {
                    resourceRegularBytes = readResourceBytes(FONT_REGULAR_PATH)
                    resourceBoldBytes = readResourceBytes(FONT_BOLD_PATH)
                }
            } catch (e: Throwable) {
                if (debugFont && isFontDebugEnabled()) {
                    Logger.e("TovTalkWithHebrewFont: font load failed: ${e.message}", "FontDebug", e)
                }
            }
        }
    }

    // On Web (hasWebUrls) never use resource TTF — only woff2 from URL work in Skiko.
    val haveResourceFonts = !hasWebUrls && resourceRegularBytes != null && resourceBoldBytes != null
    val haveWebFonts = hasWebUrls &&
        latinRegularBytes != null && latinBoldBytes != null &&
        hebrewRegularBytes != null && hebrewBoldBytes != null

    var webFontsReadyToShow by remember { mutableStateOf(false) }
    LaunchedEffect(haveWebFonts) {
        if (haveWebFonts) {
            if (debugFont && isFontDebugEnabled()) {
                Logger.d("TovTalkWithHebrewFont: haveWebFonts=true, delay 250ms then webFontsReadyToShow=true", "FontDebug")
            }
            delay(250)
            webFontsReadyToShow = true
        } else {
            webFontsReadyToShow = false
        }
    }

    // Web: two separate families; warm up both in Skiko before showing app (fixes gibberish after refresh).
    if (haveWebFonts && webFontsReadyToShow) {
        val lr = latinRegularBytes!!
        val lb = latinBoldBytes!!
        val hr = hebrewRegularBytes!!
        val hb = hebrewBoldBytes!!
        val latinFontFamily = remember(lr, lb) {
            FontFamily(
                fontFromBytes(WEB_FONT_LATIN_REGULAR_ID, lr, FontWeight.Normal, FontStyle.Normal),
                fontFromBytes(WEB_FONT_LATIN_BOLD_ID, lb, FontWeight.Bold, FontStyle.Normal)
            )
        }
        val hebrewFontFamily = remember(hr, hb) {
            FontFamily(
                fontFromBytes(WEB_FONT_HEBREW_REGULAR_ID, hr, FontWeight.Normal, FontStyle.Normal),
                fontFromBytes(WEB_FONT_HEBREW_BOLD_ID, hb, FontWeight.Bold, FontStyle.Normal)
            )
        }
        var warmupDone by remember { mutableStateOf(false) }
        LaunchedEffect(latinFontFamily, hebrewFontFamily) {
            if (debugFont && isFontDebugEnabled()) {
                Logger.d("TovTalkWithHebrewFont: FontWarmup started, delay 300ms then content()", "FontDebug")
            }
            // Extra delay so Skiko commits font registration before first content frame (fixes gibberish after refresh).
            delay(300)
            warmupDone = true
            if (debugFont && isFontDebugEnabled()) {
                Logger.d("TovTalkWithHebrewFont: warmupDone=true, showing content()", "FontDebug")
            }
        }
        CompositionLocalProvider(
            LocalTovTalkLatinFontFamily provides latinFontFamily,
            LocalTovTalkHebrewFontFamily provides hebrewFontFamily
        ) {
            if (!warmupDone) {
                FontWarmup(latinFontFamily, hebrewFontFamily)
            } else {
                content()
            }
        }
        return
    }
    if (haveResourceFonts) {
        val r = resourceRegularBytes!!
        val b = resourceBoldBytes!!
        val fontFamily = remember(r, b) {
            FontFamily(
                fontFromBytes(FONT_REGULAR_PATH, r, FontWeight.Normal, FontStyle.Normal),
                fontFromBytes(FONT_BOLD_PATH, b, FontWeight.Bold, FontStyle.Normal)
            )
        }
        CompositionLocalProvider(
            LocalTovTalkFontFamily provides fontFamily,
            LocalTovTalkHebrewFontFamily provides fontFamily
        ) {
            content()
        }
        return
    }
    // Web: fonts still loading — show splash so app is not rendered without font (same as language selection).
    if (hasWebUrls) {
        FontLoadingSplash()
        return
    }
    content()
}

/** Forces Skiko to register both fonts by drawing with them. Logo shown (no app name). */
@Composable
private fun FontWarmup(latinFontFamily: FontFamily, hebrewFontFamily: FontFamily) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(TovTalkBrand),
        contentAlignment = Alignment.Center
    ) {
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            Box(
                modifier = Modifier
                    .size(120.dp)
                    .clip(RoundedCornerShape(24.dp))
                    .background(
                        androidx.compose.ui.graphics.Brush.linearGradient(
                            colors = listOf(TovTalkBrand, TovTalkBrandLight)
                        )
                    ),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    imageVector = Icons.Default.PlayArrow,
                    contentDescription = null,
                    modifier = Modifier.size(56.dp),
                    tint = Color.White
                )
            }
            Spacer(modifier = Modifier.height(24.dp))
            CircularProgressIndicator(modifier = Modifier.size(32.dp), color = Color.White, strokeWidth = 3.dp)
        }
        // Invisible warmup: Latin + Hebrew at real size so Skiko lays out and registers both fonts before content().
        Box(modifier = Modifier.size(1.dp).graphicsLayer { alpha = 0f }) {
            Text("A", fontFamily = latinFontFamily, fontSize = 1.sp)
            Text("א", fontFamily = hebrewFontFamily, fontSize = 1.sp)
        }
        Box(modifier = Modifier.size(1.dp).graphicsLayer { alpha = 0f }) {
            Text(
                text = "עברית",
                fontFamily = hebrewFontFamily,
                fontSize = 16.sp,
                fontWeight = FontWeight.Normal
            )
        }
    }
}

/** Minimal splash shown while Hebrew font is loading on Web – logo only (no app name). */
@Composable
private fun FontLoadingSplash() {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(TovTalkBrand),
        contentAlignment = Alignment.Center
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Box(
                modifier = Modifier
                    .size(120.dp)
                    .clip(RoundedCornerShape(24.dp))
                    .background(
                        androidx.compose.ui.graphics.Brush.linearGradient(
                            colors = listOf(TovTalkBrand, TovTalkBrandLight)
                        )
                    ),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    imageVector = Icons.Default.PlayArrow,
                    contentDescription = null,
                    modifier = Modifier.size(56.dp),
                    tint = Color.White
                )
            }
            Spacer(modifier = Modifier.height(24.dp))
            LinearProgressIndicator(
                modifier = Modifier
                    .fillMaxWidth(0.4f)
                    .height(4.dp)
                    .padding(horizontal = 48.dp),
                color = Color.White
            )
        }
    }
}
